import { getIntl } from 'umi';
import { transformEnumMemberToKey } from '@/utils/core/data-transformation';
/**
 * Formats a dynamicFieldsList value for display in details view
 * @param {Object} options - Configuration options
 * @param {string} options.key - Field key name (e.g., 'emails', 'parameters')
 * @param {Array} options.value - Array of items to format
 * @param {Array} options.fieldConfig - Field configuration from form (contains fields array)
 * @param {string} options.labelKey - Translation key for the field label (e.g., 'connections.sendgrid.emails')
 * @param {Object} options.specialHandlers - Optional special handlers for specific field types
 * @param {Function} options.specialHandlers.enumHandler - Function to handle enum fields: (fieldName, value, fieldConfig) => string
 * @param {Function} options.specialHandlers.switchHandler - Function to handle switch fields: (fieldName, value, fieldConfig) => string
 * @param {Function} options.specialHandlers.fileHandler - Function to handle file fields: (item, index) => Object
 * @returns {Array} Array of formatted detail objects
 */
export const formatDynamicFieldsListForDetails = ({
  key,
  value,
  fieldConfig,
  labelKey,
  specialHandlers = {},
}) => {
  if (!Array.isArray(value) || value.length === 0) {
    return [];
  }
  const intl = getIntl();
  const details = [];
  // Get fields configuration from fieldConfig
  const fields = fieldConfig?.fields || [];
  value.forEach((item, idx) => {
    // Check if this is a file handler case (like additionalAgentVectors)
    if (specialHandlers.fileHandler) {
      const fileDetail = specialHandlers.fileHandler(item, idx);
      if (fileDetail) {
        // Ensure value is always a string, never an object
        const detailValue =
          typeof fileDetail.value === 'string' ? fileDetail.value : String(fileDetail.value || '');
        details.push({
          name: `${key}_${idx}`,
          label: `${intl.formatMessage({ id: labelKey })} ${idx + 1}`,
          value: detailValue,
          viewComponent: fileDetail.viewComponent,
          viewProps: fileDetail.viewProps,
          type: fileDetail.type || 'text',
          locale: fileDetail.locale !== undefined ? fileDetail.locale : false,
        });
        return;
      }
    }
    // Build composed value from all fields
    const parts = [];
    fields.forEach((field) => {
      const fieldValue = item?.[field.name];
      if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
        return;
      }
      let displayValue = fieldValue;
      // Handle enum fields (like position in parameters)
      if (field.type === 'select' && field.options && specialHandlers.enumHandler) {
        displayValue = specialHandlers.enumHandler(field.name, fieldValue, field);
        if (displayValue !== null && displayValue !== undefined) {
          const fieldLabel = field.label ? intl.formatMessage({ id: field.label }) : field.name;
          parts.push(`${fieldLabel}: ${displayValue}`);
        }
        return;
      }
      // Handle switch fields (like confidential in parameters)
      if (field.type === 'switch' && specialHandlers.switchHandler) {
        displayValue = specialHandlers.switchHandler(field.name, fieldValue, field);
        if (displayValue !== null && displayValue !== undefined) {
          const fieldLabel = field.label ? intl.formatMessage({ id: field.label }) : field.name;
          parts.push(`${fieldLabel}: ${displayValue}`);
        }
        return;
      }
      // Default: use field label and value
      const fieldLabel = field.label ? intl.formatMessage({ id: field.label }) : field.name;
      parts.push(`${fieldLabel}: ${displayValue}`);
    });
    if (parts.length > 0) {
      // Ensure value is always a string, never an object
      const composedValue = parts.join(' | ');
      const stringValue =
        typeof composedValue === 'string' ? composedValue : String(composedValue || '');
      details.push({
        name: `${key}_${idx}`,
        label: `${intl.formatMessage({ id: labelKey })} ${idx + 1}`,
        value: stringValue,
        type: 'text',
        locale: false,
      });
    }
  });
  return details;
};
/**
 * Helper function to create enum handler for dynamicFieldsList
 * @param {Object} enumSource - Enum object (e.g., ApiParameterPosition)
 * @param {string} translationPrefix - Translation key prefix (e.g., 'connections.api.position')
 * @returns {Function} Enum handler function
 */
export const createEnumHandler = (enumSource, translationPrefix) => {
  return (fieldName, value, fieldConfig) => {
    const intl = getIntl();
    if (typeof value === 'number') {
      const enumKey = Object.keys(enumSource)
        .find((k) => enumSource[k] === value)
        ?.toLowerCase();
      if (enumKey) {
        return intl.formatMessage({ id: `${translationPrefix}.${enumKey}` });
      }
    } else if (typeof value === 'string') {
      const enumKey = transformEnumMemberToKey(value);
      if (enumKey) {
        return intl.formatMessage({ id: `${translationPrefix}.${enumKey}` });
      }
    }
    return String(value ?? '');
  };
};
/**
 * Helper function to create switch handler for dynamicFieldsList
 * @param {string} trueLabelKey - Translation key for true value
 * @param {string} falseLabelKey - Translation key for false value
 * @returns {Function} Switch handler function
 */
export const createSwitchHandler = (trueLabelKey, falseLabelKey) => {
  return (fieldName, value, fieldConfig) => {
    const intl = getIntl();
    return value
      ? intl.formatMessage({ id: trueLabelKey })
      : intl.formatMessage({ id: falseLabelKey });
  };
};
/**
 * Helper function to create file handler for dynamicFieldsList
 * @param {string} labelKey - Translation key for the field label
 * @returns {Function} File handler function
 */
export const createFileHandler = (labelKey) => {
  return (item, index) => {
    // Ensure item is an object
    if (!item || typeof item !== 'object') {
      return null;
    }
    // Extract fileName - ensure it's a string
    const fileName = item.fileName || item.name;
    if (!fileName) return null;
    // Ensure value is always a string, never an object
    const valueString = typeof fileName === 'string' ? fileName : String(fileName || '');
    // Extract downloadUrl with fallbacks
    const downloadUrl = item.downloadUrl || item.documentTemporaryUrl || item.documentUrl || '';
    return {
      value: valueString,
      viewComponent: 'file',
      viewProps: {
        fileName: valueString,
        downloadUrl: downloadUrl,
        contentType: item.contentType || '',
      },
      type: 'text',
      locale: false,
    };
  };
};
/**
 * Cleans and transforms a dynamicFieldsList value for payload
 * Automatically filters valid items and maps to clean objects based on field configuration
 * @param {Object} options - Configuration options
 * @param {string} options.key - Field key name (e.g., 'emails', 'parameters')
 * @param {Array} options.value - Array of items to clean
 * @param {Object} options.fieldConfig - Field configuration from form (contains fields array)
 * @param {Object} options.defaultValues - Optional default values for fields (e.g., { confidential: false })
 * @param {Function} options.customFilter - Optional custom filter function: (item) => boolean
 * @param {Function} options.customMapper - Optional custom mapper function: (item) => Object
 * @returns {Array|null} Cleaned array or null if empty/invalid
 */
export const cleanDynamicFieldsListForPayload = ({
  key,
  value,
  fieldConfig,
  defaultValues = {},
  customFilter,
  customMapper,
}) => {
  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }
  // Get field names from configuration
  const fields = fieldConfig?.fields || [];
  const fieldNames = fields.map((f) => f.name);
  // Use custom mapper if provided
  if (customMapper) {
    const cleaned = value
      .filter((item) => (customFilter ? customFilter(item) : item != null))
      .map(customMapper)
      .filter((item) => item != null);
    return cleaned.length > 0 ? cleaned : null;
  }
  // Default: filter and map based on field configuration
  const cleaned = value
    .filter((item) => {
      if (customFilter) {
        return customFilter(item);
      }
      // Default filter: item exists and has at least one of the configured fields
      if (!item || typeof item !== 'object') {
        return false;
      }
      return fieldNames.some((fieldName) => {
        const fieldValue = item[fieldName];
        return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
      });
    })
    .map((item) => {
      const cleanedItem = {};
      // Map only the fields defined in configuration
      fieldNames.forEach((fieldName) => {
        const fieldValue = item[fieldName];
        // Include field if it has a value, or if there's a default value
        if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
          cleanedItem[fieldName] = fieldValue;
        } else if (defaultValues.hasOwnProperty(fieldName)) {
          cleanedItem[fieldName] = defaultValues[fieldName];
        } else {
          // Include field even if empty (for optional fields)
          cleanedItem[fieldName] = fieldValue;
        }
      });
      // Only return item if it has at least one non-null field
      const hasValidFields = Object.values(cleanedItem).some(
        (val) => val !== null && val !== undefined && val !== '',
      );
      return hasValidFields ? cleanedItem : null;
    })
    .filter((item) => item != null);
  return cleaned.length > 0 ? cleaned : null;
};
